import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { bool, func, object } from 'prop-types';
import { FocusScope } from 'react-aria';
import clsx from 'clsx';
import { AnalyticsContext } from '../../context/analyticsContext';
import { DATA_TYPES } from '../../constants';
import { StyledPanelRow, StyledDiv, slideOut, onDomHidden } from './styles';
import MobileMenuPanel from '../MobileMenuPanel';
import MobileMenuBase from '../MobileMenuBase';
import { isOpen, scrim } from '../UserTools/styles';

/**
 * Opens a new sub menu
 * @callback OpenSubMenuFn
 * @param {object[]} childMenuItems data that comes from the CMS
 * @param {string} parentSectionName label for return to parent menu button
 * @param {string} userClickedTitle title of the sub menu
 * @param {React.MutableRefObject<HTMLElement>} linkRef react ref for the parent button that opened this menu
 * used to return focus to that link when menu closes if it was opened with keyboard nav
 * @param {string} url for the title of the sub menu
 */

/**
 * @typedef FocusContextType
 * @property {OpenSubMenuFn} openSubMenu
 * @property {React.MutableRefObject<HTMLElement>} [curFocusRef]
 */

/**
 * Returns mobile menu data based on type of the data
 * @param {*} data - Mobile menu data
 */
export const getMainMenuLink = data => {
  if (data?.label) {
    return data;
  } else if (data?.children?.[0]?.type === DATA_TYPES.HEADER) {
    return data.children[0];
  } else {
    return data.children?.[0]?.children?.[0];
  }
};

/**
 * in order to create the references we need to return focus to the buttons that spawn menus,
 * our component structure has gotten a little deep. This context lets us pass state
 * modification down a couple steps without needing to pass props multiple times
 * @type {React.Context<FocusContextType>}
 */
export const FocusContext = createContext();

/**
 * The primary mobile menu of the nav. Contains First Menu and sub menus
 * @param {object} props
 * @param {Boolean} props.isMobileNavDrawerOpen
 * @param {function(boolean)} props.setMobileNavDrawer used to close the menu
 * @param {boolean} props.shouldFocusMobileMenuBtn used to focus the button that opened the menu
 * @returns {React.ReactElement}
 */
const MobileMenu = ({ isMobileNavDrawerOpen, setMobileNavDrawer, shouldFocusMobileMenuBtn }) => {
  const { analyticsClient } = useContext(AnalyticsContext);
  const [selectedMenuIndex, setSelectedMenuIndex] = useState(0);
  const [showContent, setShowContent] = useState(false);
  const [subMenuData, setSubMenuData] = useState([]);
  // stores references for buttons that opened sub menus so we can return focus when sub menus close
  const focusRefs = useRef([]);
  /**
   * indicates if focus should be returned to a button that opened a sub menu
   * needed because of how focus is managed by FocusScope
   */
  const [shouldFocus, setShouldFocus] = useState(null);

  /**
   * @type {OpenSubMenuFn}
   */
  const openSubMenu = (childMenuItems, parentSectionName, userClickedTitle, linkRef, url) => {
    const childMenuData = {
      childMenuItems,
      title: userClickedTitle,
      returnTo: parentSectionName,
      url,
    };
    setSubMenuData([...subMenuData, childMenuData]);
    focusRefs.current = [...focusRefs.current, linkRef];
    setShouldFocus(null);
  };
  const closeSubMenu = () => {
    setSelectedMenuIndex(selectedMenuIndex - 1);
  };

  // Closes entire mobile menu
  const closeMobileMenu = () => {
    analyticsClient?.clickCloseMobileMenu();
    // setting state to default
    setSelectedMenuIndex(0);
    setSubMenuData([]);
    focusRefs.current = [];
    setShowContent(false);
    // returning focus to the button that opened the mobile menu
    shouldFocusMobileMenuBtn.current = true;
  };

  /**
   * if we have a button that opened a sub menu in our state, we need to queue up
   * setting that focus next rerender. If we set focus now, it will be dropped to the body tag
   */
  useEffect(() => {
    if (focusRefs.current.length > selectedMenuIndex) {
      const tf = focusRefs.current[focusRefs.current.length - 1];
      focusRefs.current = [...focusRefs.current.slice(0, selectedMenuIndex)];
      setShouldFocus(tf);
    }
  }, [selectedMenuIndex]);

  // here's where that next rerender is managed and we can finally focus the button
  useEffect(() => {
    shouldFocus?.current.focus();
  }, [shouldFocus]);

  /**
   * TODO fix this. When a sub menu closes we change the menu we're looking at by
   * decrementing selectMenuIndex. This animates the menu moving everything right
   * but we're seeing the sub menu get unmounted before the animation is done.
   * We need to wait until the animation is done to remove the sub menu from the DOM.
   * Alternatively we could leave the sub menu in the DOM and when we add a new sub menu
   * replace the old next sub menu with the new one. We'll need to alter how focus
   * is set since we can't rely on FocusScope's restoreFocus functionality.
   * Make sure focus is restored to the button that opened the sub menu
   */
  useEffect(() => {
    if (selectedMenuIndex < subMenuData.length) {
      // removes last subMenuData in the array and decrements the selected index
      setSubMenuData([...subMenuData.slice(0, selectedMenuIndex)]);
    }
  }, [selectedMenuIndex]);

  // When the subMenuData length increases we want to render the element and then update the selectedMenuIndex
  useEffect(() => {
    if (selectedMenuIndex < subMenuData.length) {
      setSelectedMenuIndex(subMenuData.length);
    }
  }, [subMenuData.length]);

  // render once to get the menu on the dom, render again to animate the menu
  useEffect(() => {
    setShowContent(isMobileNavDrawerOpen);
  }, [isMobileNavDrawerOpen]);

  const containerClasses = clsx(
    'panel-rows',
    isMobileNavDrawerOpen && onDomHidden,
    showContent && slideOut,
  );

  return (
    <FocusContext.Provider
      value={{
        openSubMenu,
        // TODO: always undefined, should be focusRefs.current[selectedMenuIndex]? Not sure what impact it's having
        curFocusRef: focusRefs[selectedMenuIndex],
      }}
    >
      <StyledDiv
        className={containerClasses}
        data-testid={'mobile-menu-container'}
        onTransitionEnd={() => !showContent && setMobileNavDrawer(false)}
      >
        <StyledPanelRow slideLeftTimes={selectedMenuIndex} title={'main'}>
          <FocusScope contain={true} restoreFocus={true}>
            <MobileMenuBase
              closeMobileMenu={closeMobileMenu}
              isMobileNavDrawerOpen={isMobileNavDrawerOpen}
              menuIsSelected={isMobileNavDrawerOpen && selectedMenuIndex === 0}
            />
          </FocusScope>
        </StyledPanelRow>
        {subMenuData.map((panelItems, index) => {
          return (
            <StyledPanelRow
              key={`${panelItems?.title}-${index}`}
              slideLeftTimes={selectedMenuIndex}
              title={panelItems?.title}
            >
              <FocusScope autoFocus={true} contain={true} restoreFocus={true}>
                <MobileMenuPanel
                  closeMobileMenu={closeMobileMenu}
                  closeSubMenu={closeSubMenu}
                  panelItems={panelItems}
                />
              </FocusScope>
            </StyledPanelRow>
          );
        })}
      </StyledDiv>
      <div
        className={clsx(scrim, isMobileNavDrawerOpen && isOpen)}
        data-testid="mobile-menu-scrim"
        onClick={closeMobileMenu}
      />
    </FocusContext.Provider>
  );
};

export default MobileMenu;

MobileMenu.propTypes = {
  isMobileNavDrawerOpen: bool,
  setMobileNavDrawer: func,
  shouldFocusMobileMenuBtn: object,
};
